import { useNavigate } from "@tanstack/react-router";
import { Bell, Trash2 } from "lucide-react";
import { useState } from "react";

// Relative: vite.config.ts aliases only a fixed list of top-level folders and
// `features` is not one (see the same note in AppShell).
import { useSession } from "../auth/session";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { EmptyState } from "../../components/states";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { cn } from "../../utils/cn";

import { LinkifiedText } from "./LinkifiedText";
import {
  clearAllNotificationsWithin,
  scheduleNotificationDeletion,
} from "./notification-deletion";
import { opensConnections, opensOnItself } from "./notification-target";
import { PushStatus } from "./PushStatus";
import type { Notification } from "./schemas";
import { useNotifications } from "./use-notifications";
import { APP_LOCALE } from "utils";

/**
 * The inbox bell, mounted structurally in the AppShell header so every authed
 * page carries it. Renders nothing without a session: the shell must stay
 * usable on the sign-in screen and in component tests, where nobody is signed
 * in and there is no inbox to show.
 */
export function NotificationBell() {
  const session = useSession();
  if (session === null) return null;
  return <BellForUser uid={session.uid} />;
}

function BellForUser({ uid }: { uid: string }) {
  const { state, unreadCount, markRead, markAllRead } = useNotifications(uid);
  const [open, setOpen] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const navigate = useNavigate();

  const openNotification = async (notification: Notification) => {
    setOpen(false);
    // Mark-read must not block navigation: a failed write costs a badge
    // count, not the destination.
    void markRead(notification).catch(() => undefined);
    if (opensOnItself(notification)) {
      await navigate({
        to: "/notifications/$notificationId",
        params: { notificationId: notification.id },
      });
      return;
    }
    if (opensConnections(notification)) {
      await navigate({ to: "/connections" });
      return;
    }
    await navigate({
      to: "/e/$slug",
      params: { slug: notification.eventSlug },
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount > 0
              ? `Notifications (${unreadCount} unread)`
              : "Notifications"
          }
          data-testid="notification-bell"
          className="relative max-sm:h-11 max-sm:w-11"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-status-danger px-1 text-2xs font-bold leading-none text-hb-on-orange"
            >
              {unreadCount > FEED_BADGE_CAP
                ? `${FEED_BADGE_CAP}+`
                : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-full flex-col sm:max-w-md"
        // Undo on a deletion toast is outside the sheet, and a press there
        // must not close the bell the user is working in.
        onPointerDownOutside={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest("[data-sonner-toaster]")
          )
            event.preventDefault();
        }}
      >
        <SheetHeader>
          <SheetTitle>Notifications</SheetTitle>
          <SheetDescription>
            Announcements and updates from your events.
          </SheetDescription>
        </SheetHeader>
        <PushStatus />
        {state.status === "success" && state.data.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead().catch(() => undefined)}
                className="text-sm font-medium text-muted-foreground underline-offset-2 hover:underline max-sm:min-h-11"
              >
                Mark all as read
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirmingClear(true)}
              data-testid="notification-clear-all"
              className="text-sm font-medium text-destructive-text underline-offset-2 hover:underline max-sm:min-h-11"
            >
              Clear all
            </button>
          </div>
        )}
        <ClearAllDialog
          uid={uid}
          open={confirmingClear}
          onOpenChange={setConfirmingClear}
        />
        <div className="-mx-2 flex-1 overflow-y-auto">
          {state.status === "loading" && (
            <p className="px-2 py-4 text-sm text-muted-foreground">Loading…</p>
          )}
          {state.status === "error" && (
            <p className="px-2 py-4 text-sm text-destructive-text" role="alert">
              {state.error}
            </p>
          )}
          {state.status === "success" && state.data.length === 0 && (
            <EmptyState icon={Bell} title="Nothing yet">
              Event announcements and schedule changes will appear here.
            </EmptyState>
          )}
          {state.status === "success" &&
            state.data.map((notification) => (
              // A card with a stretched title button rather than one big
              // button: the body carries links, and a link inside a button is
              // invalid markup that swallows the click. The button's ::after
              // covers the card, and the links sit above it (z-10).
              <div
                key={notification.id}
                data-testid="notification-item"
                className={cn(
                  "relative rounded-md px-2 py-3 transition-colors hover:bg-accent",
                  notification.readAt === null && "bg-lit/10",
                )}
              >
                <span className="flex items-start gap-2">
                  {notification.readAt === null && (
                    <span
                      aria-label="Unread"
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-status-danger"
                    />
                  )}
                  {/* title and body are NOT sanitised anywhere — unlike the
                      event description, which UPDATE_EVENT_DESCRIPTION runs
                      through nh3. The rules accept `<script>` in an
                      announcement payload. It is safe only because JSX escapes
                      these (LinkifiedText renders text segments and anchors,
                      never HTML); never switch either to
                      dangerouslySetInnerHTML without sanitising at the write
                      path first. */}
                  <span className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => void openNotification(notification)}
                      className="block w-full truncate text-left text-sm font-semibold text-foreground after:absolute after:inset-0 after:rounded-md after:content-[''] focus-visible:outline-hidden focus-visible:after:ring-2 focus-visible:after:ring-ring"
                    >
                      {notification.title}
                    </button>
                    <span className="block whitespace-pre-line text-sm text-muted-foreground wrap-anywhere">
                      <LinkifiedText text={notification.body} />
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {notification.createdAt
                        .toDate()
                        .toLocaleString(APP_LOCALE)}
                    </span>
                  </span>
                  {/* Above the stretched title button, like the body's
                      links, so a tap here deletes rather than opens. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete notification: ${notification.title}`}
                    data-testid="notification-delete"
                    onClick={() =>
                      scheduleNotificationDeletion(uid, notification.id)
                    }
                    className="relative z-10 -my-2 shrink-0 text-muted-foreground hover:text-destructive-text"
                  >
                    <Trash2 />
                  </Button>
                </span>
              </div>
            ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * "Clear all" deletes every notification the user has, not just the ones the
 * feed shows, so it asks first: unlike a single delete it has no undo.
 */
function ClearAllDialog({
  uid,
  open,
  onOpenChange,
}: {
  uid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const change = (next: boolean) => {
    if (busy) return;
    setError(null);
    setPending(false);
    onOpenChange(next);
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    setPending(false);
    try {
      const outcome = await clearAllNotificationsWithin(uid);
      if (outcome.status === "pending") {
        setPending(true);
        return;
      }
      if (outcome.failed > 0) {
        setError(
          `${outcome.failed} of ${outcome.deleted + outcome.failed} notifications could not be deleted. Try again.`,
        );
        return;
      }
      onOpenChange(false);
    } catch {
      setError("Your notifications could not be loaded. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Clear all notifications?</DialogTitle>
          <DialogDescription>
            This deletes every notification in your inbox, on all your devices.
            You can&apos;t undo it.
          </DialogDescription>
        </DialogHeader>
        {pending && (
          <p
            role="status"
            className="text-sm text-muted-foreground"
            data-testid="notification-clear-pending"
          >
            Will finish when you&apos;re back online.
          </p>
        )}
        {error && (
          <p
            role="alert"
            className="text-sm text-destructive-text"
            data-testid="notification-clear-error"
          >
            {error}
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => change(false)}
          >
            {pending ? "Done" : "Cancel"}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy || pending}
            onClick={() => void clear()}
            data-testid="confirm-clear-notifications"
          >
            {busy ? "Deleting…" : "Delete all"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Above this the badge saturates — the feed itself is capped at FEED_LIMIT. */
const FEED_BADGE_CAP = 49;
